import StaticPage from "@/components/StaticPage";

export default function Terms() {
  return (
    <StaticPage eyebrow="Legal" title="Terms of Service" updatedAt="July 2026">
      <p className="text-[#4A4A4A]">
        These Terms govern your use of NextVisit (&quot;the Service&quot;) operated by Brooke Tomlin doing business as NextVisit (&quot;we,&quot; &quot;us&quot;), a sole proprietorship.
        By creating an account or using the Service, you agree to be bound by them.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">1. Your account</h2>
      <p>
        You must be at least 18 years old and authorized to bind the business you&apos;re signing up on behalf of. You&apos;re responsible for keeping your password secure and for everything that happens under your account.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">2. Trial and subscriptions</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li>We offer a 14-day free trial. No credit card required to start.</li>
        <li>After the trial ends there is a 3-day grace period; after that, message sending and other write actions are paused until you upgrade.</li>
        <li>Paid plans renew automatically each billing period until you cancel. You can cancel anytime from your account settings.</li>
        <li>Refunds are handled case-by-case within 14 days of a charge — email us and we&apos;ll make it right.</li>
      </ul>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">3. What you can do with the Service</h2>
      <p>You may use NextVisit to send legitimate transactional and marketing follow-up messages to your own customers. You may not:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Import customer lists you don&apos;t have permission to email.</li>
        <li>Send unsolicited bulk email (spam), pyramid schemes, or fraudulent content.</li>
        <li>Send messages that violate applicable law — CAN-SPAM, GDPR, CASL, or your local anti-spam rules.</li>
        <li>Attempt to reverse-engineer, scrape, or overload the Service.</li>
        <li>Impersonate another person or business.</li>
      </ul>
      <p>
        We monitor for abusive patterns and may suspend accounts that harm deliverability for other customers.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">4. Ownership</h2>
      <p>
        You keep ownership of everything you upload or configure: your business info, customer lists, message templates, appointment history. We keep ownership of the Service itself and its underlying code, brand, and design.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">5. Your customers</h2>
      <p>
        When you use NextVisit to message your appointment customers, you are the data controller and we are the data processor (see <a className="text-[#183D36] underline" href="/privacy">Privacy Policy</a>). You are responsible for having a lawful basis to contact your customers and for including a valid unsubscribe mechanism in every marketing message. NextVisit adds an unsubscribe link automatically for email.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">6. SMS / text messaging</h2>
      <p>
        If you use NextVisit to send SMS (text) messages, you agree that:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>You will only enable SMS for customers who have given prior express consent to receive texts about appointment follow-ups from your business.</li>
        <li>Consent must be recorded in NextVisit (Studio checkbox or CSV import attestation) and is separate from acceptance of these Terms or our Privacy Policy.</li>
        <li>You will not condition a purchase or appointment on SMS consent.</li>
        <li>Message frequency depends on your templates (typically a few messages per visit). Msg &amp; data rates may apply for recipients.</li>
        <li>Recipients can opt out by replying <b>STOP</b> (or STOPALL, UNSUBSCRIBE, CANCEL, END, QUIT). NextVisit honors those keywords immediately.</li>
        <li>Recipients can reply <b>HELP</b> for help information.</li>
      </ul>
      <p>
        Details of the consent flow and sample message language are published at{" "}
        <a className="text-[#183D36] underline" href="/messaging-consent">bookyournextvisit.com/messaging-consent</a>.
        We may suspend SMS sending for accounts that send without consent or that harm deliverability.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">7. Availability</h2>
      <p>
        We aim for 99.9% uptime but don&apos;t promise it. Occasional maintenance windows will be announced in advance when possible. We&apos;re not liable for delivery delays caused by upstream providers (Resend, Stripe, Square) or by internet outages.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">8. Termination</h2>
      <p>
        You can cancel your account at any time. We may suspend or terminate accounts that violate these Terms, harm other customers, or that we&apos;re legally required to close. Upon cancellation, your data is deleted within 90 days (see Privacy Policy).
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">9. Warranties and limits</h2>
      <p>
        The Service is provided &quot;as is.&quot; We do not warrant that it will be error-free or uninterrupted. To the maximum extent allowed by law, our aggregate liability under these Terms is capped at the amount you paid us in the 12 months preceding the claim.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">10. Changes to these Terms</h2>
      <p>
        We may update these Terms occasionally. Material changes will be announced by email at least 30 days before they take effect. Continued use of the Service after that date means you accept the updated Terms.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">11. Contact</h2>
      <p>
        Questions? Email <a className="text-[#183D36] underline" href="mailto:brooke@bookyournextvisit.com">brooke@bookyournextvisit.com</a>.
      </p>
    </StaticPage>
  );
}
